import Seo from "@/components/Seo";
import { useState } from "react";
import axios from "axios";
import { BRAND } from "@/data/mockData";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Clock, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/enquiries`, { ...form, source: "contact" });
      toast("Message sent", { description: "We'll reply within a business day." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch { toast("Couldn't send", { description: "Please try again." }); }
    finally { setBusy(false); }
  };

  const whatsappHref = `https://wa.me/${BRAND.phoneRaw}?text=${encodeURIComponent("Hello SIRIJA, I have an enquiry.")}`;
  const mapEmbed = `https://www.google.com/maps?q=${encodeURIComponent(BRAND.address)}&output=embed`;

  return (
    <div data-testid="page-contact" className="pt-32 pb-24">
      <Seo path="/contact" title="Contact & Visit" description="Visit SIRIJA at 691, Raviwar Peth, Pune. Call +91 86511 16111, WhatsApp, email or fill our enquiry form." />
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="overline">Contact</div>
          <h1 className="mt-6 font-serif font-light text-5xl lg:text-7xl leading-[1] tracking-tight">
            Say hello. <span className="italic silver-shimmer">Or drop in.</span>
          </h1>
          <p className="mt-8 text-[15px] text-[#2E2E2E]/75 max-w-xl font-light">
            The showroom is in the heart of Raviwar Peth — walk in, or write to us and we'll set aside a table.
          </p>
        </div>

        <div className="mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left: info */}
          <div className="lg:col-span-5 space-y-6">
            <div className="p-7 rounded-2xl border border-[#EDEDED] bg-[#F8F9FA]">
              <div className="flex items-start gap-4">
                <MapPin className="h-5 w-5 mt-1 text-[#2E2E2E]" strokeWidth={1.5} />
                <div>
                  <div className="overline">Address</div>
                  <div className="mt-1 text-sm leading-relaxed">{BRAND.address}</div>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <a href={`tel:${BRAND.phone.replace(/\s/g,'')}`} data-testid="contact-call" className="p-6 rounded-2xl border border-[#EDEDED] hover:bg-[#F8F9FA] transition-colors">
                <Phone className="h-5 w-5" strokeWidth={1.5} />
                <div className="overline mt-4">Call us</div>
                <div className="mt-1 text-sm">{BRAND.phone}</div>
              </a>
              <a href={whatsappHref} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="p-6 rounded-2xl border border-[#EDEDED] hover:bg-[#F8F9FA] transition-colors">
                <MessageCircle className="h-5 w-5" strokeWidth={1.5} />
                <div className="overline mt-4">WhatsApp</div>
                <div className="mt-1 text-sm">Chat with us</div>
              </a>
              <a href={`mailto:${BRAND.email}`} data-testid="contact-email" className="p-6 rounded-2xl border border-[#EDEDED] hover:bg-[#F8F9FA] transition-colors">
                <Mail className="h-5 w-5" strokeWidth={1.5} />
                <div className="overline mt-4">Email</div>
                <div className="mt-1 text-sm">{BRAND.email}</div>
              </a>
              <div className="p-6 rounded-2xl border border-[#EDEDED]">
                <Clock className="h-5 w-5" strokeWidth={1.5} />
                <div className="overline mt-4">Hours</div>
                {BRAND.hours.map(h => (
                  <div key={h.day} className="mt-1 text-xs text-[#2E2E2E]/80">
                    <span className="block">{h.day}</span>
                    <span className="block text-[#2E2E2E]/60">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden border border-[#EDEDED] aspect-[16/10]">
              <iframe
                data-testid="contact-map"
                title="SIRIJA on Google Maps"
                src={mapEmbed}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form onSubmit={submit} data-testid="contact-form" className="p-8 lg:p-10 rounded-2xl border border-[#EDEDED]">
              <h3 className="font-serif text-3xl tracking-tight">Send a message</h3>
              <p className="mt-2 text-sm text-[#2E2E2E]/70">Enquire about a piece, book a visit, or ask us anything.</p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="overline">Name</label>
                  <Input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-11 rounded-lg border-[#E5E5E5]" />
                </div>
                <div>
                  <label className="overline">Phone</label>
                  <Input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 h-11 rounded-lg border-[#E5E5E5]" />
                </div>
                <div>
                  <label className="overline">Email</label>
                  <Input required type="email" data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-11 rounded-lg border-[#E5E5E5]" />
                </div>
                <div>
                  <label className="overline">Subject</label>
                  <Input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2 h-11 rounded-lg border-[#E5E5E5]" />
                </div>
              </div>
              <div className="mt-4">
                <label className="overline">Message</label>
                <Textarea required rows={5} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 rounded-lg border-[#E5E5E5]" />
              </div>
              <button type="submit" disabled={busy} data-testid="contact-submit" className="btn-fill mt-6 h-12 px-7 rounded-full bg-[#1A1A1A] text-white text-xs uppercase tracking-[0.2em] hover:text-white disabled:opacity-60">
                {busy ? "Sending…" : "Send message"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
